import axios from 'axios';
import { roleAllowed } from '../utils/roles';

// Use relative URL in production (works with Caddy), absolute URL in development
const API_BASE_URL =
  process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

class AuthService {
  constructor() {
    this.token = localStorage.getItem('token');
    this.user = JSON.parse(localStorage.getItem('user') || 'null');

    // Set default authorization header if token exists
    if (this.token) {
      this.setAuthToken(this.token);
    }
  }

  /**
   * Set authorization token in axios headers
   */
  setAuthToken(token) {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      this.token = token;
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      this.token = null;
      localStorage.removeItem('token');
    }
  }

  /**
   * Set user data in localStorage
   */
  setUser(user) {
    this.user = user;
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }

  /**
   * Login user
   * @param {string} username - Username
   * @param {string} password - User password
   * @returns {Promise<{user: object, token: string}>}
   */
  async login(username, password) {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password,
      });

      const { token, user } = response.data;
      this.setAuthToken(token);
      this.setUser(user);

      return { user, token };
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.error || 'Failed to login');
    }
  }

  /**
   * Logout user
   */
  async logout() {
    try {
      // Call logout endpoint if token exists
      if (this.token) {
        await axios.post(`${API_BASE_URL}/auth/logout`);
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if API call fails
    } finally {
      this.setAuthToken(null);
      this.setUser(null);
    }
  }

  /**
   * Get current user info
   * @returns {Promise<object>}
   */
  async getCurrentUser() {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`);
      const user = response.data.user;
      this.setUser(user);
      return user;
    } catch (error) {
      console.error('Get current user error:', error);
      // If token is invalid, clear it
      if (error.response?.status === 401) {
        this.setAuthToken(null);
        this.setUser(null);
      }
      throw new Error(
        error.response?.data?.error || 'Failed to get current user'
      );
    }
  }

  /**
   * Default Video Footage folders for Sleep Videos or AI Stories (from GET /me or login).
   * @param {'sleepVideos'|'aiStories'} feature
   * @returns {{ id: string, name?: string }[]}
   */
  getDefaultFootageFoldersForFeature(feature) {
    const list = this.user?.defaultFootageFolders?.[feature];
    return Array.isArray(list) ? list : [];
  }

  /**
   * PATCH /auth/me/default-footage-folders — updates stored user.defaultFootageFolders.
   * @param {{ sleepVideos?: string[], aiStories?: string[] }} payload
   */
  async patchDefaultFootageFolders(payload) {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/auth/me/default-footage-folders`,
        payload,
      );
      const { defaultFootageFolders } = response.data;
      const u = this.getUser();
      if (u && defaultFootageFolders) {
        this.setUser({ ...u, defaultFootageFolders });
      }
      return defaultFootageFolders;
    } catch (error) {
      console.error('patchDefaultFootageFolders error:', error);
      throw new Error(
        error.response?.data?.error || 'Failed to update default footage folders',
      );
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!this.token && !!this.user;
  }

  /**
   * Get current user
   * @returns {object|null}
   */
  getUser() {
    return this.user;
  }

  /**
   * Get user role
   * @returns {string|null}
   */
  getUserRole() {
    return this.user?.role || null;
  }

  /**
   * Check if user has required role
   * @param {string|string[]} roles - Required role(s)
   * @returns {boolean}
   */
  hasRole(roles) {
    if (!this.user) return false;
    const userRole = this.user.role;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleAllowed(userRole, roleArray);
  }

  /**
   * Check if user can upload
   * @returns {boolean}
   */
  canUpload() {
    return this.hasRole(['uploader', 'ugcCreator', 'editor']);
  }

  /**
   * Check if user can download
   * @returns {boolean}
   */
  canDownload() {
    return this.hasRole(['superadmin', 'admin', 'uploader', 'ugcCreator', 'editor']);
  }

  /**
   * Log in as another user (superadmin only)
   * @param {string} userId - Target user ID
   * @returns {Promise<{user: object, token: string}>}
   */
  async loginAsUser(userId) {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/users/${userId}/login-as`);
      const { token, user } = response.data;
      this.setAuthToken(token);
      this.setUser(user);
      return { user, token };
    } catch (error) {
      console.error('Login as user error:', error);
      throw new Error(error.response?.data?.error || 'Failed to log in as user');
    }
  }
}

export default new AuthService();
